import { useEffect, useState } from "react";
import { BookOpen, ListChecks, Phone, Sparkles } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

type Locale = "sv" | "en" | "es";

const COPY: Record<
  Locale,
  { title: string; intro: string; items: string[]; note: string; button: string }
> = {
  sv: {
    title: "Välkommen till kunskapsbanken",
    intro:
      "Nu är allt upplåst. Här samlar vi det vi själva hade velat veta innan flytten till Costa del Sol.",
    items: [
      "Fördjupade guider om flytten, papperen och vardagen",
      "Checklistor i rätt ordning – NIE, bostad, skola och sjukvård",
      "Kostnadsbilder och kontakt- och nummerlistor att spara i telefonen",
    ],
    note:
      "Länken i mejlet är personlig – spara den så kommer du in igen, även på en annan enhet. Behöver du en ny länk fyller du bara i samma e-postadress igen.",
    button: "Börja läsa",
  },
  en: {
    title: "Welcome to the knowledge hub",
    intro:
      "Everything is unlocked. This is what we wish we had known before moving to the Costa del Sol.",
    items: [
      "In-depth guides on the move, the paperwork and daily life",
      "Checklists in the right order – NIE, housing, school and healthcare",
      "Cost overviews plus contact and phone lists to save on your phone",
    ],
    note:
      "The link in your email is personal – keep it and you can get back in, also on another device. Need a new link? Just enter the same email address again.",
    button: "Start reading",
  },
  es: {
    title: "Bienvenido/a al centro de recursos",
    intro:
      "Ya está todo desbloqueado. Aquí reunimos lo que nos habría gustado saber antes de mudarnos a la Costa del Sol.",
    items: [
      "Guías detalladas sobre la mudanza, los trámites y el día a día",
      "Listas en el orden correcto: NIE, vivienda, colegio y sanidad",
      "Costes y listas de contactos y teléfonos para guardar en el móvil",
    ],
    note:
      "El enlace del correo es personal: guárdalo y podrás entrar de nuevo, también desde otro dispositivo. Si necesitas uno nuevo, introduce el mismo correo otra vez.",
    button: "Empezar a leer",
  },
};

const ICONS = [BookOpen, ListChecks, Phone];

export function ResourceWelcomeDialog({ locale, show }: { locale: Locale; show: boolean }) {
  const t = COPY[locale];
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!show) return;
    setOpen(true);
    // Ta bort flaggan ur adressfältet så modalen inte dyker upp igen vid omladdning.
    if (typeof window !== "undefined") {
      const url = new URL(window.location.href);
      url.searchParams.delete("welcome");
      window.history.replaceState(null, "", url.pathname + url.search + url.hash);
    }
  }, [show]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-2xl">
            <Sparkles className="size-5 text-primary" aria-hidden />
            {t.title}
          </DialogTitle>
        </DialogHeader>
        <p className="leading-relaxed text-muted-foreground">{t.intro}</p>
        <ul className="mt-2 grid gap-3">
          {t.items.map((item, i) => {
            const Icon = ICONS[i] ?? BookOpen;
            return (
              <li key={item} className="flex items-start gap-3 rounded-sm border border-border bg-card p-3">
                <Icon className="mt-0.5 size-4 shrink-0 text-primary" aria-hidden />
                <span className="text-sm leading-relaxed">{item}</span>
              </li>
            );
          })}
        </ul>
        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{t.note}</p>
        <div className="mt-2 flex justify-end">
          <Button className="h-11" onClick={() => setOpen(false)}>
            {t.button}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
